(function (scope) {
  'use strict';
  const TTL = 60000;
  const list = value => Array.isArray(value) ? value : [];
  const budgets = { upto_20: [0, 2000], range_21_40: [2000, 4000], range_40_plus: [4000, Infinity] };
  const occasions = { appreciation: 'Appreciation', birth: 'New baby', home: 'New home', nikah: 'Nikah', pilgrimage: 'Pilgrimage', achievement: 'Achievement', just_because: 'Just because' };
  const recipients = { man: 'for him', woman: 'for her', child: 'for a child', couple: 'for a couple' };
  const budgetLabels = { upto_20: 'up to \u00a320', range_21_40: 'over \u00a320 to \u00a340', range_40_plus: 'over \u00a340' };
  const roleLabels = { main: 'Main gift', card: 'Greeting card', bag: 'Gift bag', add_on: 'Optional extra' };
  const PAGE_SIZE = 5;

  function eligible(product, state, role) {
    const occasion = state.occasion === 'home' ? 'new_home' : state.occasion;
    if (!budgets[state.budget] || !occasions[state.occasion] || !recipients[state.recipient]) return false;
    if (product.enabled !== true || product.excluded === true || product.imageSafe !== true || product.complete !== true) return false;
    if (!list(product.roles).includes(role) || !list(product.occasions).includes(occasion)) return false;
    const targets = list(product.recipients);
    if (!targets.includes(state.recipient) && !(targets.includes('unisex') && !targets.includes('child') && state.recipient !== 'child')) return false;
    if (role === 'main' || role === 'add_on') {
      if (state.recipient === 'child') {
        const age = state.occasion === 'birth' ? 'baby_newborn' : 'older_child';
        const ages = age === 'baby_newborn' ? ['baby_newborn', 'all_child'] : ['older_child', 'all_child', 'child', 'child_or_learner', 'older_child_or_adult'];
        if (!ages.includes(product.age)) return false;
      } else if (['baby_newborn', 'older_child', 'all_child', 'child'].includes(product.age)) return false;
    } else if (product.age === 'baby_newborn' && state.occasion !== 'birth') return false;
    if (role === 'main' && product.budgetOverride) {
      const aliases = { upto_20: 'upto_20', under_20: 'upto_20', '21_40': 'range_21_40', range_21_40: 'range_21_40', '40_plus': 'range_40_plus', range_40_plus: 'range_40_plus' };
      if (aliases[product.budgetOverride] !== state.budget) return false;
    }
    return true;
  }

  function candidates(products, state, role) {
    const [min, max] = budgets[state.budget] || [0, 0];
    return products.filter(p => eligible(p, state, role)).map(product => {
      const variants = list(product.variants).filter(v => v.available === true && Number.isSafeInteger(v.id) && Number.isInteger(v.price) && v.price > 0 && /^https?:\/\/|^\/\//.test(v.image || '') && (role !== 'main' || (v.price > min && v.price <= max)) && !(state.occasion === 'birth' && role !== 'main' && /\b(boy|girl|son|daughter)\b/i.test(v.title)));
      variants.sort((a, b) => (role === 'main' ? b.price - a.price : a.price - b.price) || Number(b.preferred === true) - Number(a.preferred === true) || a.id - b.id);
      const variant = variants[0];
      const exact = list(product.recipients).includes(state.recipient) ? 1 : 0;
      const priority = Math.max(0, Math.min(100, Number(product.priority) || 0));
      // Price and recipient specificity help within relevant choices; neither can overwhelm suitability.
      const score = priority + exact * 3 + (variant ? Math.min(10, Math.log2(1 + variant.price / 100) * 2) : 0);
      return { product, variant, role, exact, priority, score };
    }).filter(item => item.variant).sort((a, b) => {
      if (role === 'main') return b.score - a.score || b.variant.price - a.variant.price || a.product.id - b.product.id;
      return list(a.product.occasions).length - list(b.product.occasions).length || b.priority - a.priority || a.variant.price - b.variant.price || a.product.id - b.product.id;
    });
  }

  function unique(items, limit, used = new Set()) {
    const selected = [];
    for (const item of items) {
      const key = String(item.product.id);
      if (used.has(key)) continue;
      used.add(key); selected.push(item);
      if (selected.length === limit) break;
    }
    return selected;
  }

  function diverse(items, shown = []) {
    const pending = unique(items, Infinity);
    const counts = new Map();
    const family = item => item.product.family || item.product.group || String(item.product.id);
    shown.forEach(item => counts.set(family(item), (counts.get(family(item)) || 0) + 1));
    const ordered = [];
    while (pending.length) {
      const minimum = Math.min(...pending.map(item => counts.get(family(item)) || 0));
      const index = pending.findIndex(item => (counts.get(family(item)) || 0) === minimum);
      const item = pending.splice(index, 1)[0];
      ordered.push(item);
      counts.set(family(item), minimum + 1);
    }
    return ordered;
  }

  function extrasFor(products, state, main) {
    const used = new Set(main.map(i => String(i.product.id)));
    const extras = [];
    for (const role of ['card', 'bag', 'add_on']) {
      let items = candidates(products, state, role);
      if (role === 'add_on') items = items.filter(i => main.some(m => m.product.family && (i.product.family === m.product.family || i.product.family === m.product.family + '_accessory')));
      extras.push(...unique(items, 1, used));
    }
    return main.length ? extras : [];
  }

  function recommend(products, state) {
    const allMain = diverse(candidates(products, state, 'main'));
    const main = allMain.slice(0, PAGE_SIZE);
    return { main, allMain, extras: extrasFor(products, state, main) };
  }

  async function freshBatch(products, state, shown, readProduct, limit = PAGE_SIZE) {
    const shownIds = new Set(shown.map(item => item.product.id));
    const updated = new Map(products.map(product => [product.id, product]));
    const pending = diverse(candidates(products, state, 'main').filter(item => !shownIds.has(item.product.id)), shown);
    const main = [];
    for (const item of pending) {
      const fresh = await readProduct(item.product);
      if (!fresh) { updated.delete(item.product.id); continue; }
      if (fresh.id !== item.product.id) throw new Error('Unexpected gift identity');
      updated.set(fresh.id, fresh);
      const valid = candidates([fresh], state, 'main')[0];
      if (valid) main.push(valid);
      if (main.length === limit) break;
    }
    const current = [...updated.values()];
    const combined = [...shown, ...diverse(main, shown)];
    const used = new Set(combined.map(item => item.product.id));
    const remaining = candidates(current, state, 'main').filter(item => !used.has(item.product.id)).length;
    return { main: combined, added: combined.length - shown.length, remaining, products: current };
  }

  async function freshExtras(products, state, main, readProduct) {
    if (!main.length) return [];
    const used = new Set(main.map(item => item.product.id));
    const extras = [];
    const compatible = item => main.some(gift => gift.product.family && (item.product.family === gift.product.family || item.product.family === gift.product.family + '_accessory'));
    for (const role of ['card', 'bag', 'add_on']) {
      for (const item of candidates(products, state, role)) {
        if (used.has(item.product.id) || (role === 'add_on' && !compatible(item))) continue;
        const fresh = await readProduct(item.product);
        if (!fresh) continue;
        if (fresh.id !== item.product.id) throw new Error('Unexpected gift identity');
        const valid = candidates([fresh], state, role)[0];
        if (!valid || (role === 'add_on' && !compatible(valid))) continue;
        extras.push(valid); used.add(fresh.id); break;
      }
    }
    return extras;
  }

  async function readPage(url, fetcher) {
    const response = await fetcher(url, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000), headers: { Accept: 'text/html' } });
    if (!response.ok) {
      const error = new Error('Gift catalogue unavailable (' + response.status + ')');
      error.status = response.status;
      throw error;
    }
    const text = await response.text();
    if (!text.trim().startsWith('{')) throw new Error('Unexpected catalogue response');
    const data = JSON.parse(text);
    if (data.version !== 1 || data.currency !== 'GBP') throw new Error('Unsupported gift catalogue');
    return data;
  }

  async function loadCatalogue(base, fetcher = scope.fetch.bind(scope)) {
    const stamp = Date.now();
    const urlFor = page => base + '&page=' + page + '&gift_refresh=' + stamp;
    const first = await readPage(urlFor(1), fetcher);
    if (first.page !== 1 || !Number.isInteger(first.total) || first.total > 25000 || first.total < 0 || !Number.isInteger(first.pages) || first.pages < 1 || first.pages > 100) throw new Error('Incomplete catalogue');
    const pages = [first];
    let next = 2;
    await Promise.all([0, 1].map(async () => {
      while (next <= first.pages) {
        const number = next++;
        const data = await readPage(urlFor(number), fetcher);
        if (data.page !== number || data.pages !== first.pages || data.total !== first.total) throw new Error('Catalogue changed during loading');
        pages.push(data);
      }
    }));
    const scanned = pages.flatMap(p => list(p.scanned));
    if (scanned.length !== first.total || new Set(scanned).size !== first.total) throw new Error('Catalogue pagination incomplete');
    const products = pages.flatMap(p => list(p.products));
    if (new Set(products.map(p => p.id)).size !== products.length) throw new Error('Duplicate catalogue records');
    return { products, pages: first.pages, total: first.total, loadedAt: Date.now() };
  }

  const api = { eligible, candidates, diverse, extrasFor, recommend, freshBatch, freshExtras, loadCatalogue, TTL, PAGE_SIZE };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!scope.document || scope.iiGiftFinderDynamic) return;
  scope.iiGiftFinderDynamic = api;
  const catalogueCache = new Map();
  function catalogue(base, force) {
    const cached = catalogueCache.get(base);
    if (!force && cached && Date.now() - cached.started < TTL) return cached.promise;
    const entry = { started: Date.now(), promise: loadCatalogue(base) };
    catalogueCache.set(base, entry);
    entry.promise.catch(() => { if (catalogueCache.get(base) === entry) catalogueCache.delete(base); });
    return entry.promise;
  }

  function mount(root) {
    if (root.dataset.giftMounted) return;
    root.dataset.giftMounted = 'true';
    const home = root.dataset.giftSurface === 'home';
    const attrs = home ? { answer: 'data-home-answer', step: 'data-home-step', back: 'data-home-back', restart: 'data-home-restart', results: 'data-home-gift-results', title: 'data-home-result-title', copy: 'data-home-result-copy', grid: 'data-home-result-grid', addons: 'data-home-addons', addonGrid: 'data-home-addon-grid' } : { answer: 'data-answer', step: 'data-step', back: 'data-gift-back', restart: 'data-gift-restart', results: 'data-gift-results', title: 'data-gift-result-title', copy: 'data-gift-result-copy', grid: 'data-gift-grid', addons: 'data-gift-addons', addonGrid: 'data-gift-addon-grid' };
    const one = name => root.querySelector('[' + attrs[name] + ']');
    const all = name => root.querySelectorAll('[' + attrs[name] + ']');
    const steps = ['occasion', 'recipient', 'budget'];
    const state = { occasion: null, recipient: null, budget: null };
    const money = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
    let current = 'occasion';
    let request = 0;
    let shown = [];
    const base = root.dataset.giftFeed;
    const result = one('results');
    const status = one('copy');
    status.setAttribute('role', 'status');
    one('title').tabIndex = -1;
    const controls = document.createElement('div');
    controls.className = 'ii-gift-dynamic__controls';
    const count = document.createElement('p');
    const more = document.createElement('button');
    more.type = 'button'; more.textContent = 'More gift ideas'; more.hidden = true;
    more.dataset.giftMore = '';
    const grid = one('grid');
    grid.id = root.id + '-main-gifts';
    more.setAttribute('aria-controls', grid.id);
    controls.append(count, more); one('addons').after(controls);
    const finishingNote = document.createElement('p');
    finishingNote.className = 'ii-gift-dynamic__availability';
    finishingNote.dataset.giftFinishingNote = '';
    finishingNote.hidden = true;
    one('addonGrid').before(finishingNote);
    function updateFinishing() {
      const roles = [...one('addonGrid').querySelectorAll('[data-gift-role]')].map(node => node.dataset.giftRole);
      const missing = [];
      if (!roles.includes('card')) missing.push('No suitable greeting card is available for these choices just now.');
      if (!roles.includes('bag')) missing.push('No gift bag is available for these choices just now.');
      finishingNote.textContent = missing.join(' ');
      finishingNote.hidden = !missing.length;
      one('addonGrid').hidden = !roles.length;
      one('addons').hidden = !shown.length;
    }
    function show(step, focus) {
      current = step;
      all('step').forEach(node => { node.hidden = node.getAttribute(attrs.step) !== step; node.classList.toggle('is-active', !node.hidden); });
      all('answer').forEach(button => button.setAttribute('aria-pressed', String(state[button.getAttribute(attrs.answer)] === button.dataset.value)));
      root.querySelectorAll('[class$="__progress"] span').forEach((dot, i) => dot.classList.toggle('is-active', i <= steps.indexOf(step)));
      one('back').hidden = step === 'occasion';
      all('restart').forEach(button => { button.hidden = !state.occasion; });
      if (focus) root.querySelector('[' + attrs.step + '="' + step + '"] button')?.focus({ preventScroll: true });
    }
    function card(item) {
      const { product, variant, role } = item;
      const a = document.createElement('a');
      a.className = home ? 'ii-gift-home__card' : 'ii-gift-card';
      a.dataset.giftProduct = product.handle;
      a.dataset.giftVariant = String(variant.id);
      a.dataset.giftPrice = String(variant.price);
      a.dataset.giftRole = role;
      a.href = product.url + '?variant=' + variant.id;
      const image = document.createElement('img');
      image.src = variant.image; image.alt = product.title + (variant.title === 'Default Title' ? '' : ' - ' + variant.title); image.loading = 'lazy'; image.width = 600; image.height = 600;
      const body = document.createElement('div');
      const title = document.createElement('strong'); title.textContent = product.title;
      const price = document.createElement('span'); price.textContent = money.format(variant.price / 100);
      const options = document.createElement('small'); options.textContent = variant.title === 'Default Title' ? roleLabels[role] : variant.title;
      const action = document.createElement('small'); action.textContent = role === 'card' ? 'Choose card' : role === 'bag' ? 'Choose bag' : 'View gift'; action.className = 'ii-gift-dynamic__link';
      const media = document.createElement('span'); media.className = 'ii-gift-dynamic__media'; media.append(image);
      if (role !== 'main') {
        const label = document.createElement('small'); label.className = 'ii-gift-dynamic__role'; label.textContent = roleLabels[role]; body.append(label);
      }
      body.append(title, price);
      if (role === 'main' || variant.title !== 'Default Title') body.append(options);
      body.append(action); a.append(media, body);
      image.addEventListener('error', () => {
        if (!a.isConnected) return;
        a.remove();
        if (role !== 'main') updateFinishing();
      });
      return a;
    }
    async function render(append = false) {
      const token = ++request;
      result.hidden = false; result.setAttribute('aria-busy', 'true');
      more.disabled = true;
      if (!append) {
        shown = [];
        grid.replaceChildren(); one('addonGrid').replaceChildren(); one('addons').hidden = true;
        count.textContent = ''; more.hidden = true;
      }
      one('title').textContent = occasions[state.occasion] + ' gifts ' + recipients[state.recipient] + ', ' + budgetLabels[state.budget];
      status.textContent = append ? 'Checking more gift ideas...' : 'Checking current gift availability...';
      if (!append) {
        one('title').focus({ preventScroll: true });
        result.scrollIntoView({ behavior: scope.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
      }
      const selection = { ...state };
      try {
        const data = await catalogue(base, append);
        if (token !== request) return;
        const freshPool = new Map(data.products.map(product => [product.id, product]));
        const retained = append ? shown.map(item => freshPool.has(item.product.id) ? candidates([freshPool.get(item.product.id)], selection, 'main')[0] : null).filter(Boolean) : [];
        const checked = new Map();
        const readProduct = async product => {
          if (token !== request) throw new Error('Selection changed');
          if (checked.has(product.id)) return checked.get(product.id);
          let fresh;
          try {
            const payload = await readPage(product.url + '?view=gift-finder-data&gift_refresh=' + Date.now(), scope.fetch.bind(scope));
            fresh = payload.product;
          } catch (error) {
            if (error.status !== 404) throw error;
            fresh = null;
          }
          checked.set(product.id, fresh);
          return fresh;
        };
        // Fill each page from the complete eligible pool, replacing changed or sold-out candidates.
        const batch = await freshBatch(data.products, selection, retained, readProduct);
        const extras = await freshExtras(batch.products, selection, batch.main, readProduct);
        if (token !== request) return;
        shown = batch.main;
        grid.replaceChildren(...shown.map(card));
        one('addonGrid').replaceChildren(...extras.map(card));
        updateFinishing();
        count.textContent = shown.length ? 'Showing ' + shown.length + ' of ' + (shown.length + batch.remaining) + ' matching gifts.' : '';
        more.hidden = !batch.remaining;
        status.textContent = shown.length ? 'Gift ideas within your main-gift budget. Check the size or colour on the product page.' : 'No suitable gifts match these choices just now. Try another budget or browse all gifts.';
        if (append) {
          const firstNew = grid.querySelectorAll('[data-gift-product]')[retained.length];
          (firstNew || one('title')).focus({ preventScroll: true });
          firstNew?.scrollIntoView({ behavior: scope.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
        }
        const kicker = root.querySelector('[data-gift-result-kicker]'); if (kicker) kicker.textContent = 'Suggested edit';
        root.dataset.giftCatalogueTotal = String(data.total);
        root.dataset.giftCataloguePages = String(data.pages);
        root.dataset.giftCatalogueEligible = String(data.products.length);
        root.dataset.giftMatches = String(shown.length + batch.remaining);
        root.dataset.giftShown = String(shown.length);
        root.dataset.giftResultState = shown.length ? 'ready' : 'empty';
        delete root.dataset.giftError;
      } catch (error) {
        if (token !== request) return;
        shown = []; grid.replaceChildren(); one('addonGrid').replaceChildren(); one('addons').hidden = true;
        count.textContent = ''; more.hidden = true;
        root.dataset.giftShown = '0'; root.dataset.giftMatches = '0';
        root.dataset.giftError = error.message;
        status.textContent = 'We could not check the gift selection just now. Please try again or browse all gifts.';
        root.dataset.giftResultState = 'error';
      } finally { if (token === request) { result.setAttribute('aria-busy', 'false'); more.disabled = false; } }
    }
    more.addEventListener('click', () => { if (result.getAttribute('aria-busy') !== 'true') render(true); });
    all('answer').forEach(button => button.addEventListener('click', () => {
      ++request; result.hidden = true; root.dataset.giftResultState = 'choosing';
      const key = button.getAttribute(attrs.answer); state[key] = button.dataset.value;
      all('answer').forEach(b => b.classList.toggle('is-selected', state[b.getAttribute(attrs.answer)] === b.dataset.value));
      if (key === 'occasion') { state.recipient = null; state.budget = null; catalogue(base).catch(() => {}); show('recipient', true); }
      else if (key === 'recipient') { state.budget = null; show('budget', true); }
      else { show('budget', false); render(); }
    }));
    all('back').forEach(button => button.addEventListener('click', () => { ++request; result.hidden = true; show(steps[Math.max(0, steps.indexOf(current) - 1)], true); }));
    all('restart').forEach(button => button.addEventListener('click', () => { ++request; state.occasion = state.recipient = state.budget = null; result.hidden = true; all('answer').forEach(b => b.classList.remove('is-selected')); show('occasion', true); }));
    show('occasion', false);
  }
  const init = () => document.querySelectorAll('[data-gift-surface]').forEach(mount);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})(typeof window !== 'undefined' ? window : globalThis);
